import {domainReturnPath, signupPlanPath} from '../src/signup-route';
import {useEffect, useRef, useState} from 'react';
import type {HermesState} from './lib/types';
import type {BillingPayment} from '../src/billing-payment-types';
import {PRODUCT_ENTRY_LABEL} from '../src/product';
import type {hermesActions} from './lib/store';
import {APP_BASE, Link, navigate} from './router';
import {ErrorNotice, Loading} from './components';
import './account-setup.css';

export function AccountSetupPage({state, actions, planId: requestedPlanId, payment}: {
  state: HermesState; actions: typeof hermesActions; planId: string | null; payment?: BillingPayment;
}) {
  const domainDestination=domainReturnPath(payment?.returnPath??window.location.pathname+window.location.search,APP_BASE);
  const subscribeDestination = signupPlanPath(window.location.pathname + window.location.search, APP_BASE);
  const planId = requestedPlanId ?? state.session.data?.onboarding.planId ?? null;
  const accountId = state.session.data?.accountId;
  const requestId = useRef(crypto.randomUUID());
  const [attempt, setAttempt] = useState(0);
  // A receipt opened after setup is complete must not start setup or leave the receipt.
  // An open setup stays mounted through payment fulfillment until its chat is ready.
  const [continueToChat] = useState(() => !payment?.fulfilledAt);
  const complete = !continueToChat && Boolean(payment?.fulfilledAt);
  const preparation = state.accountPreparation.data;
  const membership = state.accountBilling.data?.membership;
  const billing = state.accountBilling.data;
  const paid = Boolean(payment?.paymentVerifiedAt) || billing?.paidSetup?.planId===planId || (billing?.subscription?.planId===planId && billing.subscription.status==='active');
  const plan = state.billingPlans.data?.plans.find(plan => plan.id === planId);
  const error = state.operation.scope === 'setup' ? state.operation.error : null;
  const busy = state.operation.label !== null;
  const validPlan = planId !== null && /^[0-9a-f-]{36}$/i.test(planId);
  useEffect(() => {
    if (!accountId || !validPlan || !continueToChat) return;
    const controller = new AbortController();
    // StrictMode's discarded mount must not submit an operation.
    const timer = window.setTimeout(() => {
      void actions.setupAccount(planId, requestId.current, controller.signal, attempt > 0).then(result => {
        if (controller.signal.aborted) return;
        navigate(result.billing ? `${APP_BASE}/billing?${new URLSearchParams({plan: planId, ...(domainDestination ? {next: domainDestination} : {})})}`
          : domainDestination??`${APP_BASE}/workspace?${new URLSearchParams({chat: result.sessionId})}`, true);
      }).catch(() => { /* The shared setup operation owns the complete error. */ });
    }, 0);
    return () => {window.clearTimeout(timer); controller.abort();};
  }, [accountId, planId, validPlan, actions, attempt,domainDestination,continueToChat]);

  if (!validPlan) return <section className="account-setup"><p className="eyebrow">Welcome to EightBots</p>
    <h1>First, choose your team’s allowance.</h1><p>Your account is created. Select a plan and we’ll guide you through the rest.</p>
    <Link className="button" href={subscribeDestination}>Choose a plan</Link></section>;

  const planDone = complete || paid || membership?.planId === planId;
  if (!planDone) return <section className="account-setup"><Loading active={!error}/><ErrorNotice error={error}/>
    {error && <Link className="button" href={subscribeDestination}>Choose a plan</Link>}</section>;
  const serverDone = complete || planDone && (preparation?.state === 'ready' || (preparation?.phase === 'team' && preparation.state !== 'pending'));
  const teamDone = complete || planDone && preparation?.state === 'ready';
  const current = !planDone ? 0 : !serverDone ? 1 : !teamDone ? 2 : 3;
  const planName = payment?.planName ?? plan?.name;
  const Title = payment ? 'h2' : 'h1', StepTitle = payment ? 'h3' : 'h2';
  const steps = [
    {title: planName ? `Activating ${planName}` : 'Activating your subscription', description: membership?.source==='promotion' ? `Your promotional code is applied. ${membership.planName} is included until ${new Date(membership.endsAt!).toLocaleDateString()}.` : paid ? 'Your payment is confirmed.' : billing?.configuration.planActivationEnabled ? 'Saving your selected plan. Payment is waived during early access.' : 'Confirming your subscription payment.', done: planDone},
    {title: 'Creating your private server', description: 'Preparing space for your files, projects and conversations.', done: serverDone},
    {title: 'Setting up your AI team', description: 'Preparing eight agents and your first chat.', done: teamDone},
    {title: 'Opening your workspace', description: 'Confirming your included credits and connecting your first chat.', done: complete},
  ];
  return <section className={`account-setup${payment?' account-setup-inline':''}`} aria-labelledby="setup-title">
    <Loading active={busy&&!complete}/>
    {!payment&&<p className="eyebrow">Account created</p>}
    {membership?.source==='promotion'&&<p>Thank you for joining the community!</p>}
    <Title id="setup-title" className="setup-title">{complete?'Your team has moved in.':'Your team is moving in.'}</Title>
    <p className="setup-intro">{complete?'Your server space, AI team and credits are ready.':'We’re preparing your server space, AI team and credits. Your chat opens here when it is ready.'}</p>
    <ol className="setup-steps" aria-label="Account setup progress">
      {steps.map((step, index) => <li key={index} data-state={step.done ? 'complete' : current === index ? error ? 'error' : 'current' : 'waiting'}
        aria-current={!complete && current === index ? 'step' : undefined}>
        <div><div className="setup-step-heading"><StepTitle>{step.title}</StepTitle><span>{step.done ? 'Ready' : current === index ? error ? 'Needs attention' : 'In progress' : 'Up next'}</span></div>
          <p>{step.description}</p></div>
      </li>)}
    </ol>
    {!complete&&<div className="setup-status" role="status" aria-live="polite" aria-atomic="true">
      {error ? 'Setup needs your attention. Your account and saved progress are still here.'
        : !preparation ? 'Checking your saved setup progress.'
        : preparation.state === 'pending' ? 'Your server is in the setup queue. We’ll keep this page updated.'
        : `${steps[current]!.title}. You can stay here while we finish.`}
    </div>}
    {!complete&&error && <div className="setup-error"><p>If the connection dropped, we’ll check whether your plan was already activated before continuing. Retrying won’t duplicate your credits or replace your files.</p>
      <ErrorNotice error={error}/><button className="button" type="button" disabled={busy} onClick={() => setAttempt(value => value + 1)}>Check setup and continue</button></div>}
    {complete?<Link className="button" href={domainDestination??`${APP_BASE}/workspace`}>{PRODUCT_ENTRY_LABEL}</Link>
      :<p className="setup-return-note">Need to step away? Reopen this page to continue from your saved progress.</p>}
    {!paid&&<Link className="text-button" href={subscribeDestination}>Choose a different plan</Link>}
  </section>;
}
